import { adminRequest } from "./adminAuthApi";

// ── Shared filter params ─────────────────────────────────────────────────────

export interface AuditDateRangeParams {
  search?: string;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
  page?: number;
  limit?: number;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

// ── Tab 1: Upload & Ingestion Activity (audit_log) ───────────────────────────

export interface IngestionActivityRow {
  patientName: string | null;
  action: string;
  result: string;
  resourceType: string | null;
  recordsParsed: number;
  recordsFailed: number;
  method: string | null;
  createdAt: string;
  raw: { id: string; patientId: string; s3Key: string };
}

export interface IngestionActivityResponse {
  data: IngestionActivityRow[];
  total: number;
  page: number;
  limit: number;
}

export const getIngestionActivityApi = (params: AuditDateRangeParams = {}) =>
  adminRequest<IngestionActivityResponse>(
    `/api/v1/admin/audit/ingestion${buildQuery({
      search: params.search, date_from: params.dateFrom, date_to: params.dateTo,
      page: params.page, limit: params.limit,
    })}`,
    { method: "GET", protected: true },
  );

// ── Tab 2: Login & Account Activity (auth_audit_log) ─────────────────────────

export interface AuthEventRow {
  actorName: string | null;
  actorType: "patient" | "admin" | "system";
  eventType: string;
  createdAt: string;
  raw: { id: string; actorId: string | null; ipAddress: string; userAgent: string | null; metadata: Record<string, unknown> | null };
}

export interface AuthEventsResponse {
  data: AuthEventRow[];
  total: number;
  page: number;
  limit: number;
}

export const getAuthEventsApi = (params: AuditDateRangeParams & { eventType?: string } = {}) =>
  adminRequest<AuthEventsResponse>(
    `/api/v1/admin/audit/auth-events${buildQuery({
      search: params.search, event_type: params.eventType, date_from: params.dateFrom,
      date_to: params.dateTo, page: params.page, limit: params.limit,
    })}`,
    { method: "GET", protected: true },
  );

// ── Tab 3: Record Activity (record_audit_log) ────────────────────────────────

export interface RecordEventRow {
  patientName: string | null;
  actorName: string | null;
  actorType: "patient" | "admin" | "system";
  action: "CREATE" | "UPDATE" | "DELETE" | "VIEW";
  resourceType: string;
  summary: string;
  /** One readable line per changed field, e.g. "Address: changed (value hidden for privacy)"
   *  or "Status: Active → Resolved" — render this instead of raw.fieldDiff. Empty for VIEW rows. */
  fieldChanges: string[];
  createdAt: string;
  raw: {
    id: string;
    patientId: string;
    actorId: string | null;
    resourceId: string | null;
    fieldDiff: Record<string, unknown> | null;
    ipAddress: string | null;
  };
}

export interface RecordEventsResponse {
  data: RecordEventRow[];
  total: number;
  page: number;
  limit: number;
}

export const getRecordEventsApi = (
  params: AuditDateRangeParams & { action?: string; resourceType?: string } = {},
) =>
  adminRequest<RecordEventsResponse>(
    `/api/v1/admin/audit/record-events${buildQuery({
      search: params.search, action: params.action, resource_type: params.resourceType,
      date_from: params.dateFrom, date_to: params.dateTo, page: params.page, limit: params.limit,
    })}`,
    { method: "GET", protected: true },
  );
